import { GraphQLSchema, parse, print } from 'graphql';
import { camelCase } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React from 'react';

import {
  getAncestorStates,
  getDocumentDefinitions,
  runOnlineParser,
} from 'src/app/graph/explorerPage/helpers/graphqlParseHelpers';
import {
  operationPathToGraphQLPath,
  parsePathToOperationPath,
  serializeOperationPath,
} from 'src/app/graph/explorerPage/helpers/operationPathHelpers';
import { createUniqueName } from 'src/lib/createUniqueName';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { getRootTypeNames, unwrapType } from 'src/lib/graphql/graphqlHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

function normalizeSelection(src: string) {
  return src.replace(/[ \n\t]+/g, ' ').trim();
}

const DUPLICATE_RANGE_LENGTH = 4;
export const DUPLICTE_SELECTION_SET_MESSAGE = 'Duplicate selection set found';
function getDuplicateCodeRangeActions(
  model: monacoEditor.editor.ITextModel,
  selectedRange: monacoEditor.Range,
  schema: GraphQLSchema,
): monacoEditor.languages.CodeAction[] {
  const selectionsByTypeAndPath: Record<
    string,
    Record<string, monacoEditor.Range[]>
  > = {};

  runOnlineParser(model.getValue(), (state, range) => {
    if (
      state.kind === 'Field' ||
      state.kind === 'InlineFragment' ||
      state.kind === 'FragmentSpread'
    ) {
      const operationPath = parsePathToOperationPath(getAncestorStates(state));
      const pathToFieldLastTwo = operationPathToGraphQLPath(
        operationPath,
        schema,
      )?.slice(-2);
      if (!isMinLengthArray(2, pathToFieldLastTwo)) {
        return;
      }

      const [parent] = pathToFieldLastTwo;
      const parentType = unwrapType(parent);
      const typeSelections = getOrSetRecord(
        parentType.name,
        {},
        selectionsByTypeAndPath,
      );
      const parentPath = serializeOperationPath(operationPath.slice(0, -1));
      const currentSelections = getOrSetRecord(parentPath, [], typeSelections);
      currentSelections.push(range);
    }
  });

  return Object.entries(selectionsByTypeAndPath).flatMap(
    ([fragmentType, selectionsByPath]): monacoEditor.languages.CodeAction[] => {
      // If no entries are valid, skip this type
      if (
        Object.values(selectionsByPath).every((selections) =>
          selections.every((selection) => {
            // If this is not enough or not in the selectedRange
            return (
              selection.endLineNumber - selection.startLineNumber <
                DUPLICATE_RANGE_LENGTH - 1 ||
              !selection.strictContainsRange(selectedRange)
            );
          }),
        )
      ) {
        return [];
      }

      // Keep track of normalized text, and the following ranges
      const rangesByNormalizedField: Record<
        string,
        MinLengthArray<1, { range: monacoEditor.Range }>
      > = {};
      Object.values(selectionsByPath).forEach((selections) => {
        selections.forEach((selection) => {
          const normalizedField = normalizeSelection(
            model.getValueInRange(selection),
          );
          const existingRanges = rangesByNormalizedField[normalizedField];
          if (existingRanges) {
            existingRanges.push({ range: selection });
          } else {
            rangesByNormalizedField[normalizedField] = [{ range: selection }];
          }
        });
      });

      const duplicateRanges: monacoEditor.languages.CodeAction[] = [];
      Object.values(rangesByNormalizedField)
        .filter((rangesForField) => rangesForField.length > 1)
        .forEach((rangesForField) => {
          // TODO
          // We can be a lot smarter here about taking the longest set of
          // matching fields, not just a big field
          // When we do this, make sure we dont have strict overlap with
          // the match starting at the next node
          const sampleRange = rangesForField[0].range;
          if (
            sampleRange.endLineNumber - sampleRange.startLineNumber >=
            DUPLICATE_RANGE_LENGTH - 1
          ) {
            rangesForField.forEach((rangeForField) => {
              if (!rangeForField.range.strictContainsRange(selectedRange)) {
                return;
              }
              const fragmentName = getFragmentName(fragmentType, model, schema);
              const extractedFragment = model.getValueInRange(
                rangeForField.range,
              );
              const fragment = printFragment({
                extractedFragment,
                fragmentName,
                fragmentType,
              });
              duplicateRanges.push({
                title: `Extract fragment: '${model.getValueInRange(
                  rangeForField.range,
                )}'`,
                diagnostics: [], // TODO?
                kind: 'quickfix',
                edit: {
                  edits: [
                    {
                      resource: model.uri,
                      textEdit: {
                        range: new monacoEditor.Range(1, 1, 1, 1),
                        text: fragment,
                      },
                      versionId: undefined,
                    },
                    ...rangesForField.map(
                      (range): monacoEditor.languages.IWorkspaceTextEdit => ({
                        resource: model.uri,
                        textEdit: {
                          range: range.range,
                          text: `...${fragmentName}`,
                        },
                        versionId: undefined,
                      }),
                    ),
                  ],
                },
                isPreferred: true,
              });
            });
          }
        });
      // TODO
      // Filter these duplicateRanges to exclude any that are inside another range? Or not actually
      return duplicateRanges;
    },
  );
}

function getFragmentName(
  fragmentType: string,
  model: monacoEditor.editor.ITextModel,
  schema: GraphQLSchema,
) {
  const { fragmentDefinitions } = getDocumentDefinitions(
    model.getValue(),
    getRootTypeNames(schema),
  );
  const fragmentNames = fragmentDefinitions
    .map(({ name }) => name)
    .filter((name): name is string => !!name);
  return createUniqueName(`${fragmentType}Fragment`, fragmentNames, camelCase);
}

function printFragment({
  extractedFragment,
  fragmentName,
  fragmentType,
}: {
  extractedFragment: string;
  fragmentName: string;
  fragmentType: string;
}) {
  const spacing = extractedFragment.match(/\n( *)[^\n]+$/)?.[1] ?? '';
  const fragment =
    `fragment ${fragmentName} on ${fragmentType} {\n${spacing}${extractedFragment}\n}\n\n`
      .split('\n')
      .map((line) => {
        const lineSpacing = line.match(/^( *)/)?.[1]?.length ?? 0;
        if (lineSpacing >= spacing.length) {
          return line.slice(spacing.length - 2);
        }
        return line.slice(lineSpacing);
      })
      .join('\n');
  try {
    return print(parse(fragment));
  } catch {
    return fragment;
  }
}

export function codeActionProviderFactory({
  schemaRef,
}: {
  schemaRef: React.MutableRefObject<GraphQLSchema>;
}): monacoEditor.languages.CodeActionProvider {
  return {
    provideCodeActions(model, range) {
      const actions = getDuplicateCodeRangeActions(
        model,
        range,
        schemaRef.current,
      );
      return {
        actions,
        dispose: () => {},
      };
    },
  };
}
